import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from './auth.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Project } from '../classes/project';
import { Subject } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { of } from 'rxjs/observable/of';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ProjectManagerService {
  private URL: string = environment.apiUrl;
  project: Project = new Project();
  // project: any = {};
  projectId: string = '';

  isSavedProject = new BehaviorSubject<boolean>(false);

  projectNewChanges = new Subject<string>();

  sharedProjId: string = null;

  constructor(private http: HttpClient, private auth: AuthService) {}

  private handleError(error: HttpErrorResponse, errorMessage: string) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,

      if (error instanceof Array) {
        // handle class-validator errors
        error.forEach(err => {
          const obj = err.constraints;
          if (obj) {
            for (const prop in obj) {
              if (obj.hasOwnProperty(prop)) {
                console.error('Backend error: ' + obj[prop]);
              }
            }
          }
        });
      } else if (typeof error === 'string') {
        console.error(`Backend returned message: ${error}`);
      } else console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return ErrorObservable.create(errorMessage);
  }

  resetProject(): void {
    this.project = new Project();
    this.project.scenery = 'grid';
  }

  setProject(proj: Project) {
    this.project = proj;
  }

  getCurrentProject(): Project {
    return this.project;
  }

  setTitleBlockOptions(options) {
    this.project.draw_options = {
      titleBlockOptions: options,
      items:
        this.project.draw_options && this.project.draw_options.items
          ? this.project.draw_options.items
          : {}
    };
  }

  setProToolsItems(items) {
    if (this.project.draw_options && this.project.draw_options.items !== null) {
      this.project.draw_options = {
        titleBlockOptions: this.project.draw_options.titleBlockOptions
          ? this.project.draw_options.titleBlockOptions
          : {},
        items: items
      };
    } else {
      this.project.draw_options = {
        titleBlockOptions: this.project.draw_options ? this.project.draw_options : {},
        items: items
      };
    }
  }

  getCurrentProjectId() {
    return this.project.id;
  }

  setIsSavedProject(value: boolean) {
    this.isSavedProject.next(value);
  }

  setProjectName(name: any) {
    this.project.projectName = name;
  }

  setProjectImg(img: any) {
    this.project.projectImg = img;
  }

  setTiers(tiers: Array<any>) {
    this.project.tiers = tiers;
  }

  setUserId(id: string) {
    this.project.userId = id;
  }

  setSnapshots(imgs: any) {
    this.project.snapshots = imgs;
  }

  setSnapshotsProTools(imgs: any) {
    this.project.snapshotsProTools = imgs;
  }

  setDeckHeight(value) {
    this.project.deckHeight = value;
  }

  setProjectType(value: string): void {
    this.project.projectType = value;
    this.projectNewChanges.next(value);
  }

  getProjectType(): string {
    return this.project.projectType;
  }

  setDockSubtype(value: string): void {
    this.project.dockSubtype = value;
  }

  getDockSubtype(): string {
    return this.project.dockSubtype || 'home';
  }

  buildProjectCreateBody(): any {
    const project = this.project;
    const body: any = {
      projectName: project.projectName,
      projectImg: project.projectImg,
      deckHeight: project.deckHeight,
      userId: project.userId,
      snapshots: project.snapshots || [],
      snapshotsProTools: project.snapshotsProTools || [],
      projectType: project.projectType,
      dockSubtype: project.dockSubtype,
      accessories: project.accessories || [],
      deckMaterial: project.deckMaterial,
      scenery: project.scenery,
      tiers: project.tiers || [],
      railing: project.railing
    };

    if (project.railingColors) {
      body.railingColors = project.railingColors;
    }

    if (project.draw_options) {
      body.draw_options = project.draw_options;
    }

    return body;
  }

  saveProject(): Observable<any> {
    const project = this.buildProjectCreateBody();
    return this.http
      .post(`${this.URL}/projects?access_token=${this.auth.getToken()}`, project, httpOptions)
      .pipe(
        switchMap(res => {
          this.setProject(<Project>res);
          this.setIsSavedProject(true);
          return of(res);
        }),
        catchError(err => Observable.throw(err))
      );
  }

  saveProduct(product): Observable<any> {
    return this.http.post(`${this.URL}/products`, product, httpOptions).pipe(
      switchMap(res => {
        console.log('product saved', res);
        return of(res);
      }),
      catchError(err => {
        return this.handleError(err, 'Error saving project');
      })
    );
  }

  projectExists(id: string): Observable<any> {
    return this.http.get(`${this.URL}/projects/${id}/exists`, httpOptions);
  }

  getProjects(userId?: string): Observable<any[]> {
    return this.http
      .get<any[]>(
        `${this.URL}/users/${
          userId || this.project.userId
        }/projects?access_token=${this.auth.getToken()}`,
        httpOptions
      )
      .pipe(
        map(data => {
          return data.sort((a, b) => {
            return new Date(b.updated).getTime() - new Date(a.updated).getTime();
          });
        })
      );
  }

  getAllUsersProjects(): Observable<any> {
    return this.http.get(`${this.URL}/projects`, httpOptions);
  }

  getProject(projectId: string): Observable<any> {
    return this.http.get(`${this.URL}/projects/${projectId}`, httpOptions);
  }

  updateProject(): Observable<any> {
    const { id, ...project } = this.project;
    return this.http
      .put(`${this.URL}/projects/${id}?access_token=${this.auth.getToken()}`, project, httpOptions)
      .pipe(
        switchMap(res => {
          if (res) {
            this.setProject(<Project>Object.assign({}, this.project, res));
          }
          this.setIsSavedProject(true);
          return of(res || this.project);
        }),
        catchError(err => Observable.throw(err))
      );
    // return this.http.put(`${this.URL}/projects?access_token=${this.auth.getToken()}`, this.project, httpOptions);
  }

  projectNameValid(projectName: string): Observable<any> {
    return this.http
      .get(
        `${
          this.URL
        }/is-project-name-valid?project_name=${projectName}&user_id=${this.auth.getUserId()}`,
        httpOptions
      )
      .pipe(catchError(err => this.handleError(err, 'Error checking project name is unique')));
  }

  getNewProjectName(): Observable<any> {
    return this.http
      .get(`${this.URL}/create-new-project-name?user_id=${this.auth.getUserId()}`, httpOptions)
      .pipe(
        switchMap(res => {
          return of((<any>res).projectName);
        }),
        catchError(err => {
          return this.handleError(err, 'Error getting new project name');
        })
      );
  }

  updateProjectItem(projectId: any, property: string, value: any): Observable<any> {
    let obj = {};
    obj[property] = value;
    return this.http.patch(
      `${this.URL}/projects/${projectId}?access_token=${this.auth.getToken()}`,
      obj,
      httpOptions
    );
  }

  deleteProject(projectId: any): Observable<any> {
    return this.http.delete(
      `${this.URL}/projects/${projectId}?access_token=${this.auth.getToken()}`,
      httpOptions
    );
  }

  clearProject() {
    this.project = new Project();
    this.projectId = '';
    this.isSavedProject.next(false);
  }

  getTemplates(): Observable<any> {
    return this.http.get(`${this.URL}/templates`, httpOptions);
  }

  saveTemplate(): Observable<any> {
    const { id, created, updated, ...project } = this.project;
    this.setIsSavedProject(true);
    return this.http.post(`${this.URL}/templates`, project, httpOptions);
  }

  updateTemplate(): Observable<any> {
    const { id, ...project } = this.project;
    return this.http.put(
      `${this.URL}/templates/${id}?access_token=${this.auth.getToken()}`,
      project,
      httpOptions
    );
  }

  updateTemplateItem(projectId: any, property: string, value: any): Observable<any> {
    let obj = {};
    obj[property] = value;
    return this.http.patch(
      `${this.URL}/templates/${projectId}?access_token=${this.auth.getToken()}`,
      obj,
      httpOptions
    );
  }

  deleteTemplate(projectId: any): Observable<any> {
    return this.http.delete(
      `${this.URL}/templates/${projectId}?access_token=${this.auth.getToken()}`,
      httpOptions
    );
  }

  setAccessories(accessories: Array<any>) {
    this.project.accessories = accessories;
  }

  setDeckMaterial(texture: string) {
    this.project.deckMaterial = texture;
  }

  getDeckMaterial(): string {
    return this.project.deckMaterial;
  }

  setScenery(name: string) {
    this.project.scenery = name;
  }

  getScenery() {
    return this.project.scenery;
  }

  setRailing(railingParams: any) {
    this.project.railing = railingParams;
  }

  setSharedProjID(id: string) {
    this.sharedProjId = id;
  }

  ifCurrentProjIsSharedProj(id) {
    if (id === this.sharedProjId) {
      return true;
    } else {
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/project-manager.service.ts